/**
 * `/api/rewards` payloads — the per-wallet dashboard summary.
 *
 * Envelope is `{ success, data }` / `{ success, error }`, identical to the
 * retired `get-rewards-info` Cloudflare Worker, so existing consumers did not
 * have to change when it moved onto the app's database connection.
 */
import { z } from "zod";

import { suiAddressLooseSchema } from "../primitives";

/** Query for `GET /api/rewards/info`. */
export const rewardsInfoQuerySchema = z.object({
  wallet: suiAddressLooseSchema,
});

/** Resolved rewards-info query. */
export type RewardsInfoQuery = z.infer<typeof rewardsInfoQuerySchema>;

/**
 * Dashboard summary for one wallet.
 *
 * Token amounts carry 8 decimals, percentages 2. Cashback is the exception: it
 * is USDC out of sale proceeds, recomputed here from live license prices rather
 * than read from the snapshot column, and reported at 2.
 */
export const rewardsInfoDataSchema = z.object({
  wallet: z.string(),
  /** Lifetime $TRUTH, 8 decimals. */
  totalTokens: z.number(),
  /** Percentage change against the previous snapshot. */
  totalTokensChange: z.number(),
  /** USDC cashback from referred license sales. */
  totalCashback: z.number(),
  /** How far the live cashback has drifted from the snapshot's own column. */
  cashbackChange: z.number(),
  agentPool: z.number(),
  agentPoolChange: z.number(),
  referralRewards: z.number(),
  multipleReferralRewards: z.number(),
  /** Tier factor for the wallet's license count. Must match the Python ladder. */
  tierBoost: z.number(),
});

/** Resolved rewards-info payload. */
export type RewardsInfoData = z.infer<typeof rewardsInfoDataSchema>;

/** Response for `GET /api/rewards/info`. */
export const getRewardsInfoResponseSchema = z.union([
  z.object({ success: z.literal(true), data: rewardsInfoDataSchema }),
  z.object({ success: z.literal(false), error: z.string() }),
]);

/** Resolved rewards-info response. */
export type GetRewardsInfoResponse = z.infer<typeof getRewardsInfoResponseSchema>;
